import { useState } from "react";
import { CalendarClock, CreditCard, Clock3, Gift } from "lucide-react";
import { toLocalInput } from "@/components/EventWindowCard";

const LIVE_STATES = new Set(["active_paid", "complimentary", "override"]);
const MODES = [
  { key: "pay_later", label: "Extend now, pay later", icon: CreditCard, hint: "Goes live now · payment link emailed to the organiser" },
  { key: "on_payment", label: "Extend on payment", icon: Clock3, hint: "Organiser pays from their festival page · goes live once Stripe confirms payment" },
  { key: "complimentary", label: "Complimentary extension", icon: Gift, hint: "Goes live now · no charge" },
];
export const EXTENSION_MODE_LABELS = Object.fromEntries(MODES.map((m) => [m.key, m.label]));

const inputCls = "block w-full mt-1 bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-sm text-white focus:outline-none focus:border-[#CCFF00] [color-scheme:dark]";
const labelCls = "text-[10px] uppercase tracking-widest text-[#A3A3A3]";

export function ExtendEndCard({ festival, onSubmit }) {
  const [mode, setMode] = useState("pay_later");
  const [value, setValue] = useState(() => (festival.end_at ? toLocalInput(new Date(new Date(festival.end_at).getTime() + 3600000).toISOString()) : ""));
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const ended = !festival.end_at || new Date(festival.end_at) <= new Date();
  const live = LIVE_STATES.has(festival.license_state) && !ended;
  const paid = mode !== "complimentary";
  const error = !value ? "Pick a new end time"
    : new Date(value) <= new Date(festival.end_at) ? "New end must be after the current end"
    : paid && !(Number(amount) >= 0.5) ? "Enter the extension price (min £0.50)" : null;

  return (
    <div className="card-surface p-4 mb-6" data-testid="toolkit-extend-end-card">
      <div className="flex items-center gap-2 mb-2">
        <CalendarClock className="w-4 h-4 text-[#CCFF00]" />
        <div className="label-mono text-[10px] text-[#CCFF00]">EXTEND FESTIVAL END</div>
      </div>
      <div className="text-xs text-[#A3A3A3] mb-3" data-testid="toolkit-extend-end-current">
        Current end: <span className="text-white">{festival.end_at ? new Date(festival.end_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—"}</span>
      </div>
      {!live ? (
        <p className="text-[11px] text-[#8A8A8A]" data-testid="toolkit-extend-end-unavailable">
          Only live festivals (paid, complimentary or override) that haven't ended can be extended.
        </p>
      ) : (
        <div className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-3" data-testid="toolkit-extend-modes">
            {MODES.map((m) => {
              const Icon = m.icon;
              const on = m.key === mode;
              return (
                <button key={m.key} type="button" onClick={() => setMode(m.key)} data-testid={`toolkit-extend-mode-${m.key}`}
                  className={`text-left p-3 border transition-colors ${on ? "border-[#CCFF00] bg-[#CCFF00]/5" : "border-[#262626] hover:border-[#525252]"}`}>
                  <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${on ? "text-[#CCFF00]" : "text-white"}`}>
                    <Icon className="w-3.5 h-3.5" /> {m.label}
                  </div>
                  <div className="text-[10px] text-[#A3A3A3] mt-1">{m.hint}</div>
                </button>
              );
            })}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className={labelCls}>
              New end time
              <input type="datetime-local" value={value} min={toLocalInput(festival.end_at)} onChange={(e) => setValue(e.target.value)}
                data-testid="toolkit-extend-end-input" className={inputCls} />
            </label>
            {paid ? (
              <label className={labelCls}>
                Extension price (£)
                <input type="number" min={0.5} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                  placeholder="e.g. 25.00" data-testid="toolkit-extend-amount" className={inputCls} />
              </label>
            ) : (
              <label className={labelCls}>
                Note (optional)
                <input type="text" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. goodwill gesture" data-testid="toolkit-extend-note" className={inputCls} />
              </label>
            )}
          </div>
          {error && <div className="text-[11px] text-[#FFB020]" data-testid="toolkit-extend-end-error">{error}</div>}
          <button type="button" disabled={!!error}
            onClick={() => onSubmit({ mode, new_end_at: new Date(value).toISOString(), amount: paid ? Number(amount) : null, note: paid ? null : note.trim() || null })}
            data-testid="toolkit-extend-end-submit"
            className="px-4 py-2 text-[11px] uppercase tracking-widest font-bold border border-[#CCFF00] bg-[#CCFF00] text-black hover:bg-white hover:border-white disabled:opacity-40 disabled:cursor-not-allowed">
            Review &amp; confirm
          </button>
        </div>
      )}
    </div>
  );
}
